import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, Loader2, Pencil, Trash2, X, Store, Phone, Mail, MapPin, User, LayoutGrid, List, Share2, ChevronDown, ChevronRight } from 'lucide-react';
import { useEscapeToHome } from '@/hooks/useKeyboard';
import ShareDialog from '@/components/ShareDialog';

const VENDOR_TYPES = [{value:'Car Dealer',label:'Car Dealer'},{value:'Service Center',label:'Service Center'},{value:'Accessories Store',label:'Accessories Store'},{value:'Fuel Station',label:'Fuel Station'},{value:'Insurance Agent',label:'Insurance Agent'},{value:'RTO Agent',label:'RTO Agent'},{value:'Other',label:'Other'}];
const emptyForm = { name:'', address:'', vendor_type:'', contact_persons: [{ name:'', number:'', email:'' }], notes:'' };

export default function VendorsPage() {
  useEscapeToHome();
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [selected, setSelected] = useState(new Set());
  const [viewMode, setViewMode] = useState('list');
  const [shareVendor, setShareVendor] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/vendors'); setVendors(data.vendors || []); setCategories(data.categories || []); } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.key === 'n' && !isTyping) { e.preventDefault(); openCreate(); }
      else if (e.key === 'v' && !isTyping) { e.preventDefault(); setViewMode(m => m === 'board' ? 'list' : 'board'); }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  });

  const openCreate = () => { setForm({ ...emptyForm, contact_persons: [{ name:'', number:'', email:'' }] }); setEditingVendor(null); setShowForm(true); };
  const openEdit = (v) => { setForm({ name: v.name, address: v.address||'', vendor_type: v.vendor_type||'', contact_persons: (v.contact_persons||[]).length > 0 ? v.contact_persons : [{ name:'', number:'', email:'' }], notes: v.notes||'' }); setEditingVendor(v); setShowForm(true); };

  const save = async () => {
    if (!form.name) { toast.error('Vendor name required'); return; }
    const payload = { ...form, contact_persons: form.contact_persons.filter(c => c.name || c.number) };
    try {
      if (editingVendor) { await api.put(`/vendors/${editingVendor.id}`, payload); toast.success('Vendor updated'); }
      else { await api.post('/vendors', payload); toast.success('Vendor created'); }
      setShowForm(false); fetchVendors();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteVendor = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try { await api.delete(`/vendors/${id}`); toast.success('Vendor deleted'); fetchVendors(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };

  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} vendor(s)?`)) return;
    try { const { data } = await api.post('/bulk-delete', { record_type: 'vendor', record_ids: Array.from(selected) }); toast.success(data.message); setSelected(new Set()); fetchVendors(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Group by category for board view
  const boardGroups = {};
  if (categories.length > 0) {
    categories.forEach(c => { boardGroups[c] = []; });
    boardGroups['Uncategorized'] = [];
    vendors.forEach(v => {
      if ((v.usage_categories||[]).length === 0) boardGroups['Uncategorized'].push(v);
      else (v.usage_categories||[]).forEach(c => { if (boardGroups[c]) boardGroups[c].push(v); });
    });
  }

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden -mb-20 md:-mb-6" data-testid="vendors-page">
      <div className="shrink-0 p-4 md:px-8 md:pt-6 md:pb-4 border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        <div className="flex items-center justify-between">
          <div><h2 className="text-2xl sm:text-3xl font-medium tracking-tight">Vendors</h2><p className="text-sm text-zinc-500 mt-1">{vendors.length} vendor{vendors.length !== 1 ? 's' : ''}</p></div>
          <div className="flex items-center gap-2">
            <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
              <button onClick={() => setViewMode(viewMode === 'board' ? 'list' : 'board')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'board' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="vendors-view-board">
                <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Board</button>
              <button onClick={() => setViewMode(viewMode === 'list' ? 'board' : 'list')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="vendors-view-list">
                <List className="h-3.5 w-3.5" strokeWidth={1.5} /> List</button>
            </div>
            <Button onClick={openCreate} className="rounded-xl" data-testid="create-vendor-btn"><Plus className="h-4 w-4 mr-1" /> New Vendor</Button>
            {selected.size > 0 && <Button size="sm" variant="destructive" onClick={bulkDelete} data-testid="vendors-bulk-delete"><Trash2 className="h-3 w-3 mr-1" /> Delete {selected.size}</Button>}
          </div>
        </div>
      </div>

      <div className={`flex-1 overflow-hidden ${viewMode === 'board' ? '' : 'overflow-y-auto p-4 md:px-8'}`}>
        {viewMode === 'board' ? (
          loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
          : <div className="flex gap-3 h-full overflow-x-auto px-4 md:px-6 py-4">
              {Object.entries(boardGroups).filter(([,items]) => items.length > 0).map(([cat, items]) => (
                <div key={cat} className="flex-shrink-0 w-72 md:w-0 md:flex-1 md:min-w-[220px] md:max-w-xs flex flex-col rounded-xl bg-zinc-50 dark:bg-zinc-900/50 overflow-hidden">
                  <div className="flex items-center gap-2 p-3 shrink-0"><Store className="h-3.5 w-3.5 text-zinc-400" /><span className="text-xs font-bold uppercase tracking-wider truncate">{cat}</span><span className="text-xs text-zinc-400 ml-auto">{items.length}</span></div>
                  <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-2">
                    {items.map(v => (
                      <div key={v.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 group overflow-hidden">
                        <p className="text-sm font-medium break-words">{v.name}</p>
                        {v.vendor_type && <p className="text-[10px] text-zinc-400">{v.vendor_type}</p>}
                        {v.address && <p className="text-[10px] text-zinc-400 truncate">{v.address}</p>}
                        <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100">
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => openEdit(v)}><Pencil className="h-2.5 w-2.5 mr-1" /> Edit</Button>
                          <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-red-500" onClick={() => deleteVendor(v.id)}><Trash2 className="h-2.5 w-2.5" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
        ) : (
        <div className="w-full">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
          : vendors.length === 0 ? <div className="flex flex-col items-center justify-center py-20"><Store className="h-10 w-10 text-zinc-300 mb-3" /><p className="text-zinc-500 text-sm mb-4">No vendors</p><Button onClick={openCreate} variant="outline"><Plus className="h-4 w-4 mr-1" /> New Vendor</Button></div>
          : (
            (() => {
              // Build grouped map: each vendor appears under each of its usage_categories; uncategorized bucket for vendors without any.
              const groupMap = {};
              vendors.forEach(v => {
                const cats = (v.usage_categories || []).length ? v.usage_categories : ['Uncategorized'];
                cats.forEach(c => { (groupMap[c] = groupMap[c] || []).push(v); });
              });
              const keys = Object.keys(groupMap).sort((a, b) => a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b));
              const toggleGroup = (g) => setCollapsedGroups(prev => { const n = new Set(prev); if (n.has(g)) n.delete(g); else n.add(g); return n; });
              return (
                <div className="space-y-3">
                  {keys.map(cat => {
                    const items = groupMap[cat];
                    const isOpen = !collapsedGroups.has(cat);
                    return (
                      <div key={cat} data-testid={`vendor-group-${cat}`}>
                        <button onClick={() => toggleGroup(cat)} className="flex items-center gap-2 w-full text-left py-1.5 px-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-900" data-testid={`vendor-toggle-group-${cat}`}>
                          {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                          <Badge className="bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-[10px] px-2 py-0.5">{cat}</Badge>
                          <span className="text-xs text-zinc-400">{items.length}</span>
                        </button>
                        {isOpen && (
                          <div className="mt-2 space-y-2 pl-6">
                            {items.map(v => (
                              <div key={`${cat}-${v.id}`} className={`bg-white dark:bg-zinc-900 border rounded-xl p-4 group ${selected.has(v.id) ? 'ring-2 ring-zinc-400 border-zinc-400' : 'border-zinc-200 dark:border-zinc-800'}`} data-testid={`vendor-card-${v.id}`}>
                                <div className="flex items-start gap-3">
                                  <input type="checkbox" className="rounded border-zinc-300 mt-1 shrink-0" checked={selected.has(v.id)} onChange={() => toggleSelect(v.id)} />
                                  <div className="w-10 h-10 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center shrink-0"><Store className="h-5 w-5 text-zinc-500" /></div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <h3 className="text-sm font-medium">{v.name}</h3>
                                        {v.is_shared_with_me && <Badge variant="secondary" className="text-[9px] px-1.5 bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300" data-testid={`vendor-shared-${v.id}`}>Shared</Badge>}
                                      </div>
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                        {!v.is_shared_with_me && <Button size="sm" variant="ghost" className="h-7" onClick={() => setShareVendor(v)} data-testid={`share-vendor-${v.id}`}><Share2 className="h-3 w-3" /></Button>}
                                        <Button size="sm" variant="ghost" className="h-7" onClick={() => openEdit(v)}><Pencil className="h-3 w-3" /></Button>
                                        <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => deleteVendor(v.id)}><Trash2 className="h-3 w-3" /></Button>
                                      </div>
                                    </div>
                                    {v.vendor_type && <p className="text-xs text-zinc-500">{v.vendor_type}</p>}
                                    {v.address && <p className="text-xs text-zinc-400 mt-0.5"><MapPin className="h-3 w-3 inline" /> {v.address}</p>}
                                    {(v.usage_categories||[]).length > 1 && <div className="flex gap-1 mt-1.5 flex-wrap">{v.usage_categories.filter(c => c !== cat).map(c => <Badge key={c} variant="outline" className="text-[9px] px-1.5">also in {c}</Badge>)}</div>}
                                    {(v.contact_persons||[]).filter(c => c.name).length > 0 && (
                                      <div className="mt-2 space-y-1">{v.contact_persons.filter(c => c.name).map((c, i) => (
                                        <div key={i} className="flex items-center gap-3 text-xs text-zinc-500">
                                          <span className="flex items-center gap-1"><User className="h-3 w-3" />{c.name}</span>
                                          {c.number && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.number}</span>}
                                          {c.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</span>}
                                        </div>
                                      ))}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}
        </div>
        )}
      </div>

      {selected.size > 0 && (
        <div className="fixed bottom-20 md:bottom-8 left-1/2 -translate-x-1/2 bg-zinc-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-50">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={() => setSelected(new Set())} className="text-xs underline opacity-70">Clear</button>
          <Button size="sm" variant="destructive" onClick={bulkDelete}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingVendor ? 'Edit' : 'New'} Vendor</DialogTitle><DialogDescription>Vendor details</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Vendor Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} autoFocus /></div>
              <div className="space-y-1"><Label className="text-xs">Vendor Type</Label><SearchableSelect value={form.vendor_type} onValueChange={v => setForm(p => ({...p, vendor_type: v}))} options={VENDOR_TYPES} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Address</Label><Textarea value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} rows={2} /></div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label className="text-xs">Contact Persons</Label>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setForm(p => ({...p, contact_persons: [...p.contact_persons, {name:'',number:'',email:''}]}))}><Plus className="h-3 w-3 mr-1" /> Add</Button></div>
              {form.contact_persons.map((cp, i) => (
                <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                  <Input value={cp.name} onChange={e => { const arr = [...form.contact_persons]; arr[i] = {...arr[i], name: e.target.value}; setForm(p => ({...p, contact_persons: arr})); }} placeholder="Name" className="h-8 text-xs" />
                  <Input value={cp.number} onChange={e => { const arr = [...form.contact_persons]; arr[i] = {...arr[i], number: e.target.value}; setForm(p => ({...p, contact_persons: arr})); }} placeholder="Phone" className="h-8 text-xs" />
                  <div className="flex gap-1"><Input value={cp.email} onChange={e => { const arr = [...form.contact_persons]; arr[i] = {...arr[i], email: e.target.value}; setForm(p => ({...p, contact_persons: arr})); }} placeholder="Email" className="h-8 text-xs" />
                  {form.contact_persons.length > 1 && <Button size="sm" variant="ghost" className="h-8 px-1" onClick={() => setForm(p => ({...p, contact_persons: p.contact_persons.filter((_,j) => j !== i)}))}><X className="h-3 w-3" /></Button>}</div>
                </div>
              ))}
            </div>
            <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} /></div>
          </div>
          <DialogFooter><Button onClick={save}>{editingVendor ? 'Update' : 'Create'} Vendor</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {shareVendor && <ShareDialog open={!!shareVendor} onClose={() => setShareVendor(null)} recordType="vendor" recordId={shareVendor.id} recordName={shareVendor.name} />}
    </div>
  );
}
